// src/components/Sidebar.tsx
import React from 'react';
import {
  Home,
  FileText,
  Shield,
  CreditCard,
  Settings,
  Users,
  UserCog,
  Wrench,
  Crown,
} from 'lucide-react';
import logo from '../assets/damelio_logo_blanco.png';
import logo2 from '../assets/damelio_texto_blanco.png';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  setActivePage: (page: string) => void;
  isAdmin: boolean;
  activePage: string;
}

const Sidebar: React.FC<SidebarProps> = ({ setActivePage, isAdmin, activePage }) => {
  const { t } = useTranslation();

  const menuItems = [
    { translationKey: 'sidebar.menu.home',           icon: Home,      id: 'home' },
    { translationKey: 'sidebar.menu.my_research',    icon: FileText,  id: 'myResearch' },
    { translationKey: 'sidebar.menu.my_competitors', icon: Users,     id: 'myCompetitors' },
    { translationKey: 'sidebar.menu.sienaguard',     icon: Shield,    id: 'sienaguard' },
    { translationKey: 'sidebar.menu.tools',          icon: Wrench,    id: 'tools' },
    { translationKey: 'sidebar.menu.premium',        icon: Crown,     id: 'premium' },
    { translationKey: 'sidebar.menu.payments',       icon: CreditCard,id: 'payments' },
  ];

  const sidebarBackgroundColor = 'bg-gradient-to-b from-black via-[#0A192F] to-[#060D1B]';

  return (
    <aside
      className={`min-h-screen w-60 p-4 text-white shadow-xl flex flex-col relative z-20 ${sidebarBackgroundColor}`}
    >
      {/* Logos */}
      <div
        className="mb-8 my-6 text-center cursor-pointer transition-transform duration-300"
        onClick={() => setActivePage('home')}
      >
        <img
          src={logo}
          alt={t('sidebar.logoAlt', 'Damelio Logo')}
          className="mx-auto h-24 w-auto transition-transform duration-300"
        />
        <img
          src={logo2}
          alt={t('sidebar.logoTextAlt', 'Damelio Extra Logo')}
          className="mx-auto h-14 w-auto transition-transform duration-300"
        />
      </div>

      {/* Menú */}
      <nav className="flex-grow overflow-y-auto">
        <ul>
          {menuItems.map((item) => {
            const isActive = activePage === item.id;
            return (
              <li key={item.id} className="mb-4">
                <button
                  onClick={() => setActivePage(item.id)}
                  className={`
                    flex items-center w-full px-4 py-3 text-sm rounded transition-colors
                    ${isActive ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-800'}
                  `}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  <span className="whitespace-normal text-left">
                    {t(item.translationKey)}
                  </span>
                </button>
                <hr className="border-gray-500 my-1" />
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="mt-auto">
        <hr className="border-gray-500 my-1" />

        {isAdmin && (
          <>
            <button
              onClick={() => setActivePage('admin')}
              className="flex items-center w-full px-4 py-3 text-sm rounded bg-gradient-to-r from-red-600 to-red-700 text-white mb-1"
            >
              <UserCog className="mr-3 h-5 w-5" />
              <span>{t('sidebar.menu.admin')}</span>
            </button>
            <hr className="border-gray-500 my-1" />
          </>
        )}

        <button
          onClick={() => setActivePage('settings')}
          className={`
            flex items-center w-full px-4 py-3 text-sm rounded transition-colors
            ${activePage === 'settings' ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-800'}
          `}
        >
          <Settings className="mr-3 h-5 w-5" />
          <span>{t('sidebar.menu.settings')}</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
